import React, { createContext, useState, useEffect, useContext } from 'react';

const LANGUAGE_STORAGE_KEY = 'bobatea_preferred_language';
const DEFAULT_LANGUAGE = 'en';

export const LanguageContext = createContext({ currentLang: DEFAULT_LANGUAGE, isTransitioning: false, setCurrentLang: () => { } });

// Professional localStorage utilities with error handling
const LanguageStorage = {
  get: () => {
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      return stored && ['en', 'ku', 'ar'].includes(stored) ? stored : DEFAULT_LANGUAGE;
    } catch (error) {
      console.warn('Failed to read language preference from localStorage:', error);
      return DEFAULT_LANGUAGE;
    }
  },

  set: (language) => {
    try {
      if (['en', 'ku', 'ar'].includes(language)) {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      }
    } catch (error) {
      console.warn('Failed to save language preference to localStorage:', error);
    }
  }
};

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const [currentLang, setCurrentLangState] = useState(DEFAULT_LANGUAGE);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize from localStorage on mount
  useEffect(() => {
    const savedLanguage = LanguageStorage.get();
    setCurrentLangState(savedLanguage);
    setIsInitialized(true);
  }, []);

  // Enhanced setter that saves to localStorage with smooth transition
  const setCurrentLang = (newLanguage) => {
    if (['en', 'ku', 'ar'].includes(newLanguage) && newLanguage !== currentLang) {
      setIsTransitioning(true);

      // Wait for fade out
      setTimeout(() => {
        setCurrentLangState(newLanguage);
        LanguageStorage.set(newLanguage);

        // Wait for DOM update then fade in
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 300);
    }
  };

  // Don't render until initialized to prevent flash of wrong language
  if (!isInitialized) {
    return null;
  }

  return (
    <LanguageContext.Provider value={{ currentLang, isTransitioning, setCurrentLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for easier usage
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const translations = {
  en: {
    title: "Bubble Tea Supplier\nfor Iraq & Kurdistan",
    subtitle: "Official Partner of Ibubble",
    description: "The leading Bubble Tea supplier for Iraq and Kurdistan. We provide high-quality products, professional training, and the strategic partnership to build a thriving bubble tea brand.",
    products: "Explore Collection",
    whatsapp: "Partner With Us",
    menu: { home: "Home", products: "Products", about: "About Us", contact: "Contact Us" },
    productSectionTitle: "Products",
    productSectionSubtitle: "Discover our professional assortment.",
    categories: { basePowder: "Base Powder", powder: "Tea & Powder", syrups: "Syrups", bobas: "Bobas & Jellys", tools: "Tools & Machines" },
    details: "Details",
    priceOnRequest: "Price on Request",
    contactWhatsApp: "Contact via WhatsApp",
    whatsappMessage: "Hello! I'm interested in the product: {productName}. Could you please tell me the price?",
    moreFlavorsTitle: "More Flavors Available",
    moreFlavorsDesc: "We have many more delicious flavors! Request our complete catalog.",
    moreSyrupsTitle: "More Syrups Available",
    moreSyrupsDesc: "We have many more delicious syrups! Request our complete catalog.",
    moreBobasTitle: "More Bobas & Jellys Available",
    moreBobasDesc: "We have many more delicious bobas and jellys! Request our complete catalog.",
    requestCatalog: "Request Catalog via WhatsApp",
    catalogRequest: "Hello! I would like to receive the complete flavor catalog. Thank you!",
    pagination: {
      previous: "Previous",
      next: "Next",
      page: "Page",
      of: "of",
      items: "items total",
      showAll: "Show All {category}",
      showPaginated: "Show Paginated",
      allItemsShown: "All {count} products shown"
    },
    tools: {
      hardCup500cc: { name: "500cc Hard Cup 90mm", desc: "Professional grade 500cc Hard Cup for perfect bubble tea serving." },
      hardCup700cc: { name: "700cc Hard Cup 90mm", desc: "Professional grade 700cc Hard Cup for perfect bubble tea serving." },
      lidPanda: { name: "Lid Panda", desc: "Cute Panda Lid design to seal your bubble tea cups securely." },
      straw230: { name: "Straw 230*11", desc: "Wide straw perfect for sucking up tapioca pearls and toppings." },
      powderScoop: { name: "Powder Scoop", desc: "Precise powder scoop for consistent flavor measurement." },
      longBarSpoon: { name: "Long Bar Spoon", desc: "Professional long spoon for stirring and mixing drinks." },
      bobaSpoon: { name: "Boba Spoon", desc: "Perforated spoon designed specifically for serving boba pearls." },
      jigger: { name: "Jigger", desc: "Essential measuring tool for precise syrup and liquid ratios." },
      pitcher: { name: "Pitcher", desc: "Durable pitcher for mixing and pouring tea bases." },
      shaker500cc: { name: "Shaker 500cc", desc: "Professional 500cc shaker for perfectly blended cold drinks." },
      shaker700cc: { name: "Shaker 700cc", desc: "Professional 700cc shaker for perfectly blended cold drinks." },
      plasticBag: { name: "Plastic Bag", desc: "Available in two variants: Single (for 1 cup) and Double (for 2 cups at once) for convenient takeaway." },
      shakerMachine: { name: "Shaker Machine", desc: "Automatic shaking machine to save time and ensure consistency." },
      thermos: { name: "Thermos", desc: "High-quality thermos to keep your tea base hot or cold for hours." }
    },
    syrups: {
      tigerSugar: { name: "Tiger Sugar", desc: "Rich and smoky brown sugar flavor with deep caramel notes." },
      wintermelon: { name: "Wintermelon", desc: "Fresh and sweet melon taste with a hint of caramel." },
      caramel: { name: "Caramel", desc: "Deep, buttery sweetness with a rich golden aroma." },
      hazelnut: { name: "Hazelnut", desc: "Roasted nut flavor with a sweet, earthy aroma." },
      blueberry: { name: "Blueberry", desc: "Sweet and tangy taste of fresh, ripe blueberries." },
      kumquatLemon: { name: "Kumquat Lemon", desc: "Zesty citrus blend of tart kumquat and refreshing lemon." },
      mango: { name: "Mango", desc: "Juicy, tropical sweetness of sun-ripened mangoes." },
      passionFruit: { name: "Passion Fruit", desc: "Exotic, tart, and aromatic tropical flavor." },
      peach: { name: "Peach", desc: "Delicate and floral sweetness of juicy peaches." },
      lemon: { name: "Lemon", desc: "Sharp and refreshing zesty lemon taste." },
      strawberry: { name: "Strawberry", desc: "Classic sweet and fruity taste of red strawberries." }
    },
    powders: {
      tea: { name: "Black Assam Tea", desc: "Robust and full-bodied black tea with a malty flavor." },
      nonDairyCream: { name: "Non Dairy Creamer", desc: "Rich and velvety texture enhancer for a creamy taste." },
      chocolate: { name: "Chocolate", desc: "Deep, indulgent cocoa flavor with a smooth finish." },
      coffee: { name: "Coffee", desc: "Bold and aromatic roasted coffee taste." },
      matcha: { name: "Matcha", desc: "Earthy and grassy notes of premium Japanese green tea." },
      banana: { name: "Banana", desc: "Creamy and sweet taste of ripe bananas." },
      watermelon: { name: "Watermelon", desc: "Light and refreshing sweet watermelon flavor." },
      avocado: { name: "Avocado", desc: "Rich, buttery, and smooth avocado taste." },
      coconut: { name: "Coconut", desc: "Sweet and nutty tropical coconut flavor." },
      greenApple: { name: "Green Apple", desc: "Crisp, tart, and sweet green apple taste." },
      mango: { name: "Mango", desc: "Sweet and creamy tropical mango flavor." },
      strawberry: { name: "Strawberry", desc: "Sweet and creamy taste of fresh strawberries." },
      blueberry: { name: "Blueberry", desc: "Sweet berry flavor with a hint of tartness." },
      taro: { name: "Taro", desc: "Sweet, nutty, and vanilla-like flavor with a creamy texture." },
      peach: { name: "Peach", desc: "Soft and fragrant sweet peach taste." },
      pineapple: { name: "Pineapple", desc: "Bright and tangy tropical pineapple flavor." },
      vanilla: { name: "Vanilla", desc: "Classic, smooth, and aromatic vanilla bean taste." },
      oreo: { name: "Oreo", desc: "Sweet chocolate cookie flavor with creamy notes." },
      cherry: { name: "Cherry", desc: "Bold and sweet fruity cherry flavor." },
      passionFruit: { name: "Passion Fruit", desc: "Tangy and exotic tropical fruit taste." },
      tiramisu: { name: "Tiramisu", desc: "Coffee-soaked sponge cake flavor with cocoa and mascarpone notes." },
      redVelvet: { name: "Red Velvet", desc: "Sweet cocoa and buttermilk cake flavor." },
      sweetPotato: { name: "Sweet Potato", desc: "Earthy, sweet, and nutty purple potato taste." },
      yellowHamiMelon: { name: "Yellow Hami Melon", desc: "Sweet, floral, and aromatic melon flavor." }
    },
    bobas: {
      tapioca: { name: "Tapioca", desc: "Classic chewy black tapioca pearls, the heart of bubble tea." },
      blueberryBoba: { name: "Blueberry", desc: "Popping boba filled with sweet blueberry juice." },
      strawberryBoba: { name: "Strawberry", desc: "Popping boba bursting with strawberry flavor." },
      lycheeBoba: { name: "Lychee", desc: "Floral and sweet lychee popping boba." },
      honeyDewBoba: { name: "Honey Dew", desc: "Sweet honeydew melon popping boba." },
      mangoBoba: { name: "Mango", desc: "Tropical mango popping boba for a burst of sunshine." },
      passionFruitBoba: { name: "Passion Fruit", desc: "Tangy passion fruit popping boba." },
      kiwiBoba: { name: "Kiwi", desc: "Zesty kiwi popping boba." },
      coffeeBoba: { name: "Coffee", desc: "Coffee flavored popping boba for a caffeine kick." },
      tropicalBoba: { name: "Tropical", desc: "Mixed tropical fruit popping boba." },
      pomegranateBoba: { name: "Pomegranate", desc: "Tart and sweet pomegranate popping boba." },
      cherryBoba: { name: "Cherry", desc: "Sweet cherry popping boba." },
      chocolateBoba: { name: "Chocolate", desc: "Chocolate flavored popping boba." },
      caramelBoba: { name: "Caramel", desc: "Rich caramel flavored popping boba." },
      greenAppleBoba: { name: "Green Apple", desc: "Crisp green apple popping boba." },
      peachBoba: { name: "Peach", desc: "Juicy peach popping boba." },
      raspberryBoba: { name: "Raspberry", desc: "Tart raspberry popping boba." },
      coffeeJelly: { name: "Coffee Jelly", desc: "Chewy coffee flavored jelly cubes." },
      rainbowJelly: { name: "Rainbow Jelly", desc: "Colorful mixed fruit jelly strips." },
      nataDeCoco: { name: "Nata De Coco Jelly", desc: "Chewy coconut jelly, a classic topping." },
      nataDeCocoStrawberry: { name: "Strawberry Jelly", desc: "Strawberry flavored coconut jelly." },
      nataDeCocoMango: { name: "Mango Jelly", desc: "Mango flavored coconut jelly." }
    },
    footer: {
      about: "About Us",
      aboutDesc: "Your partner for professional bubble tea business success.",
      quickLinks: "Quick Links",
      contact: "Contact",
      rights: "All rights reserved.",
      ctaTitle: "We look forward to your message!"
    }
  },
  ku: {
    title: "دابینکەری بەبڵتی\nبۆ عێراق و کوردستان",
    subtitle: "بريكاری سەرەكى Ibubble",
    description: "پێشەنگی دابینکردنی بەبڵتی بۆ عێراق و کوردستان. ئێمە بەرهەمی کوالیتی بەرز، ڕاهێنانی پیشەیی و ئامێری پێویست بۆ دەستپێکردنی بزنسەکەت دابین دەکەین.",
    products: "بەرهەمەکان",
    whatsapp: "واتساپ",
    menu: { home: "سەرەتا", products: "بەرهەمەکان", about: "دەربارەی ئێمە", contact: "پەیوەندیمان پێوە بکە" },
    productSectionTitle: "بەرهەمەکان",
    productSectionSubtitle: "کۆمەڵە هەڵبژاردەی پێشەیی بدۆزەرەوە.",
    categories: { basePowder: "پاودەری سەرەکی", powder: "پاودەر", syrups: "شروبی تام", bobas: "بۆبا و جەلی", tools: "ئامراز و ئامێرەکان" },
    details: "وردەکاری",
    priceOnRequest: "نرخ بە داواکاری",
    contactWhatsApp: "پەیوەندی لە ڕێگەی واتساپەوە",
    whatsappMessage: "سڵاو! من حەزم لەم بەرهەمەیە: {productName}. تکایە نرخەکەیم پێ بڵێن؟",
    moreFlavorsTitle: "تامی تر هەیە",
    moreFlavorsDesc: "تامی تری زۆرمان هەیە! داوای لیستی تەواو بکە",
    moreSyrupsTitle: "شرابی منکەهە تر هەیە",
    moreSyrupsDesc: "شروبی تامی تری زۆرمان هەیە! داوای لیستی تەواو بکە",
    moreBobasTitle: "بۆبا و جەلی تر هەیە",
    moreBobasDesc: "بۆبا و جەلی تری زۆرمان هەیە! داوای لیستی تەواو بکە",
    requestCatalog: "داوای لیست بکە لە واتساپ",
    catalogRequest: "سڵاو! دەمەوێت لیستی تەواوی تامەکان وەربگرم. سوپاس!",
    pagination: {
      previous: "پێشوو",
      next: "دواتر",
      page: "لاپەڕە",
      of: "لە",
      items: "بڕگە کۆی گشتی",
      showAll: "هەموو {category} نیشاندان",
      showPaginated: "بە لاپەڕە نیشاندان",
      allItemsShown: "هەموو {count} بەرهەم نیشاندراو"
    },
    syrups: {
      tigerSugar: { name: "شروبی شەکری ڕەش", desc: "تامی شەکری ڕەشی دەوڵەمەند و دوکەڵاوی." },
      wintermelon: { name: "کاڵەکی سەوز", desc: "تامی کاڵەکی شیرین و تازە." },
      caramel: { name: "کارامێل", desc: "تامی کارامێلی دەوڵەمەند و شیرین." },
      hazelnut: { name: "بندوق", desc: "تامی بندوقی برژاو و بۆنخۆش." },
      blueberry: { name: "بلووبێری", desc: "تامی شیرین و ترشی بلووبێری تازە." },
      kumquatLemon: { name: "پرتەقاڵی یابانی", desc: "تێکەڵەی تامی ترش و شیرینی پرتەقاڵ و لیمۆ." },
      mango: { name: "مانگۆ", desc: "تامی شیرینی مانگۆی گەیو." },
      passionFruit: { name: "پاشن فرووت", desc: "تامی ترش و شیرینی میوەی پاشن فرووت." },
      peach: { name: "قۆخ", desc: "تامی شیرین و بۆنخۆشی قۆخ." },
      lemon: { name: "لیمۆ", desc: "تامی ترش و تازەی لیمۆ." },
      strawberry: { name: "شیلک", desc: "تامی شیرین و سروشتی شیلک." }
    },
    tools: {
      hardCup500cc: { name: "کووپی 500ملیم", desc: "کووپی 500ملیم بە کوالیتی بەرز بۆ پێشکەشکردنی بەبڵتی." },
      hardCup700cc: { name: "کووپی 700ملیم", desc: "کووپی 700ملیم بە کوالیتی بەرز بۆ پێشکەشکردنی بەبڵتی." },
      lidPanda: { name: "سەری کووپ", desc: "سەری کووپی پاندای جوان بۆ داخستنی کووپەکان بە توندی." },
      straw230: { name: "قەسەب", desc: "قەسەبی پان بۆ هەڵمژینی بۆباکان و پێکهاتەکان." },
      powderScoop: { name: "کەوچکی پاودەر", desc: "کەوچکی پێوانەیی ورد بۆ دیاریکردنی بڕی تەواوی تام." },
      longBarSpoon: { name: "کەوچکی درێژ", desc: "کەوچکی درێژی پیشەیی بۆ تێکەڵکردنی خواردنەوەکان." },
      bobaSpoon: { name: "کەوچکی بۆبا", desc: "کەوچکی تایبەت بە بۆباکان بۆ ئاسانکاری لە پێشکەشکردن." },
      jigger: { name: "جیگەر", desc: "ئامێری پێوانەیی گرنگ بۆ دیاریکردنی بڕی شرووب و شلەمەنی." },
      pitcher: { name: "سوراحی بچووک", desc: "سوراحی بەهێز بۆ تێکەڵکردن و ڕشتنی بنچینەی چا." },
      shaker500cc: { name: "شەیکەری 500 ملیم", desc: "شەیکەری پیشەیی 500 ملیم بۆ تێکەڵکردنی تەواوی خواردنەوە ساردەکان." },
      shaker700cc: { name: "ملیم 700 شەیکەری", desc: "شەیکەری پیشەیی 700 ملیم بۆ تێکەڵکردنی تەواوی خواردنەوە ساردەکان." },
      plasticBag: { name: "عەلاگەی پلاستیکی", desc: "بە دوو جۆر بەردەستە: تاکی (بۆ ١ کووپ) و جووت (بۆ ٢ کووپ بەیەکەوە) بۆ هەڵگرتنی ئاسان." },
      shakerMachine: { name: "ئامێری شەیکەر", desc: "ئامێری شەیکەری ئۆتۆماتیکی بۆ گەڕاندنەوەی کات و دڵنیابوون لە کوالیتی." },
      thermos: { name: "تەرمۆزە", desc: "تەرمۆزەی کوالیتی بەرز بۆ پاراستنی گەرمی یان ساردی چا بۆ ماوەیەکی زۆر." }
    },
    powders: {
      tea: { name: "چای ڕەشی ئەسام", desc: "تامی چای ڕەشی بەهێز و ڕەسەن." },
      nonDairyCream: { name: "کرێمی نا شیرەمەنی", desc: "تامی کرێمی و نەرم." },
      chocolate: { name: "چۆکلێت", desc: "تامی چۆکلێتی دەوڵەمەند." },
      coffee: { name: "قاوە", desc: "تامی قاوەی برژاوی بۆنخۆش." },
      matcha: { name: "ماچا", desc: "تامی سروشتی و سەوزی چای ماچا." },
      banana: { name: "مۆز", desc: "تامی شیرین و کرێمی مۆز." },
      watermelon: { name: "شوتی", desc: "تامی شیرین و فێنکی شوتی." },
      avocado: { name: "ئاڤۆکادۆ", desc: "تامی دەوڵەمەند و کرێمی ئاڤۆکادۆ." },
      coconut: { name: "گوێزی هیندی", desc: "تامی شیرین و تایبەتی گوێزی هیندی." },
      greenApple: { name: "سێوی سەوز", desc: "تامی ترش و شیرینی سێوی سەوز." },
      mango: { name: "مانگۆ", desc: "تامی شیرین و کرێمی مانگۆ." },
      strawberry: { name: "شیلک", desc: "تامی شیرین و کرێمی شیلک." },
      blueberry: { name: "بلووبێری", desc: "تامی میوەیی بلووبێری." },
      taro: { name: "تارۆ", desc: "تامی شیرین و تایبەتی تارۆ." },
      peach: { name: "قۆخ", desc: "تامی شیرین و بۆنخۆشی قۆخ." },
      pineapple: { name: "ئەنەناس", desc: "تامی ترش و شیرینی ئەنەناس." },
      vanilla: { name: "ڤانێلا", desc: "تامی نەرم و بۆنخۆشی ڤانێلا." },
      oreo: { name: "ئۆریۆ", desc: "تامی بسکیت و کرێمی ئۆریۆ." },
      cherry: { name: "گێلاس", desc: "تامی شیرینی گێلاس." },
      passionFruit: { name: "پاشن فرووت", desc: "تامی ترش و تایبەتی پاشن فرووت." },
      tiramisu: { name: "ترامیسۆ", desc: "تامی کێک و قاوەی ترامیسۆ." },
      redVelvet: { name: "رێد ڤێڵڤێت", desc: "تامی کێکی ڕێد ڤێڵڤێت." },
      sweetPotato: { name: "پەتاتەی شیرین", desc: "تامی شیرین و تایبەتی پەتاتەی شیرین." },
      yellowHamiMelon: { name: "کاڵەکی زەرد", desc: "تامی شیرین و بۆنخۆشی کاڵەک." }
    },
    bobas: {
      tapioca: { name: "تاپیۆکا", desc: "بۆبای تاپیۆکای نەرم و بەتام بۆ سەر خواردنەوەکان." },
      blueberryBoba: { name: "بلووبێری", desc: "بۆبای بلووبێری نەرم و بەتام بۆ سەر خواردنەوەکان." },
      strawberryBoba: { name: "شیلک", desc: "بۆبای شیلکی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      lycheeBoba: { name: "لیچی", desc: "بۆبای لیچی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      honeyDewBoba: { name: "کاڵەک", desc: "بۆبای کاڵەکی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      mangoBoba: { name: "مانگۆ", desc: "بۆبای مانگۆی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      passionFruitBoba: { name: "پاشن فرووت", desc: "بۆبای پاشن فرووتی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      kiwiBoba: { name: "کیوی", desc: "بۆبای کیوی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      coffeeBoba: { name: "قاوە", desc: "بۆبای قاوەی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      tropicalBoba: { name: "میوەی تێکەڵ", desc: "بۆبای میوەی تێکەڵی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      pomegranateBoba: { name: "هەنار", desc: "بۆبای هەناری نەرم و بەتام بۆ سەر خواردنەوەکان." },
      cherryBoba: { name: "گێلاس", desc: "بۆبای گێلاسی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      chocolateBoba: { name: "چۆکلێت", desc: "بۆبای چۆکلێتی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      caramelBoba: { name: "کارامێل", desc: "بۆبای کارامێلی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      greenAppleBoba: { name: "سێوی سەوز", desc: "بۆبای سێوی سەوزی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      peachBoba: { name: "قۆخ", desc: "بۆبای قۆخی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      raspberryBoba: { name: "ڕاسبێری", desc: "بۆبای ڕاسبێری نەرم و بەتام بۆ سەر خواردنەوەکان." },
      coffeeJelly: { name: "قاوە", desc: "بۆبای قاوەی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      rainbowJelly: { name: "ڕاینبۆ", desc: "بۆبای ڕاینبۆی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      nataDeCoco: { name: "گوێزی هیندی", desc: "بۆبای گوێزی هیندی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      nataDeCocoStrawberry: { name: "گوێزی هیندی و شیلک", desc: "بۆبای گوێزی هیندی و شیلکی نەرم و بەتام بۆ سەر خواردنەوەکان." },
      nataDeCocoMango: { name: "گوێزی هیندی و مانگۆ", desc: "بۆبای گوێزی هیندی و مانگۆی نەرم و بەتام بۆ سەر خواردنەوەکان." }
    },
    footer: {
      about: "دەربارەی ئێمە",
      aboutDesc: "هاوبەشتان بۆ سەرکەوتنی بزنسی بەبڵتی.",
      quickLinks: "بەستەرە خێراکان",
      contact: "پەیوەندی",
      rights: "هەموو مافەکان پارێزراون.",
      ctaTitle: "بە خۆشحاڵییەوە چاوەڕێی نامەکەتین!"
    }
  },
  ar: {
    title: "مورد بابلتي\nللعراق وكردستان",
    subtitle: "شريك رسمي لـ Ibubble",
    description: "المورد الرائد للبابل تي في العراق وكردستان. نحن نوفر منتجات عالية الجودة، تدريبًا احترافيًا، ومعدات لبدء مشروعك الخاص.",
    products: "المنتجات",
    whatsapp: "واتساب",
    menu: { home: "الرئيسية", products: "المنتجات", about: "معلومات عنا", contact: "اتصل بنا" },
    productSectionTitle: "المنتجات",
    productSectionSubtitle: "اكتشف تشكيلتنا المهنية.",
    categories: { basePowder: "بودرة الأساس", powder: "شاي وبودرة", syrups: "شرابات", bobas: "بوبا و جيلي", tools: "أدوات وآلات" },
    details: "التفاصيل",
    priceOnRequest: "السعر عند الطلب",
    contactWhatsApp: "التواصل عبر واتساب",
    whatsappMessage: "مرحباً! أنا مهتم بالمنتج: {productName}. هل يمكنك إخباري بالسعر؟",
    moreFlavorsTitle: "نكهات أخرى متوفرة",
    moreFlavorsDesc: "لدينا نكهات أخرى كثيرة! اطلب القائمة الكاملة",
    moreSyrupsTitle: "شرابات أخرى متوفرة",
    moreSyrupsDesc: "لدينا شرابات أخرى كثيرة! اطلب القائمة الكاملة",
    moreBobasTitle: "بوبا و جيلي أخرى متوفرة",
    moreBobasDesc: "لدينا بوبا و جيلي أخرى كثيرة! اطلب القائمة الكاملة",
    requestCatalog: "اطلب القائمة عبر واتساب",
    catalogRequest: "مرحباً! أريد قائمة النكهات الكاملة. شكراً!",
    pagination: {
      previous: "السابق",
      next: "التالي",
      page: "صفحة",
      of: "من",
      items: "عنصر إجمالي",
      showAll: "عرض جميع {category}",
      showPaginated: "عرض بالصفحات",
      allItemsShown: "تم عرض جميع {count} المنتجات"
    },
    syrups: {
      tigerSugar: { name: "شراب السكر الأسود", desc: "نكهة السكر الأسود الغنية والمدخنة." },
      wintermelon: { name: "بطيخ شتوي", desc: "طعم البطيخ الشتوي الحلو والمنعش." },
      caramel: { name: "الكراميل", desc: "حلاوة الكراميل الغنية والذهبية." },
      hazelnut: { name: "بندق", desc: "نكهة البندق المحمص والعطري." },
      blueberry: { name: "توت أزرق", desc: "طعم توت أزرق الحلو والحامض." },
      kumquatLemon: { name: "برتقال ذهبي", desc: "مزيج حمضي منعش من البرتقال الذهبي والليمون." },
      mango: { name: "مانجو", desc: "حلاوة المانجو الاستوائية الغنية." },
      passionFruit: { name: "باشن فروت", desc: "نكهة باشن فروت الحامضة والمنعشة." },
      peach: { name: "خوخ", desc: "حلاوة الخوخ الناعمة والعطرية." },
      lemon: { name: "ليمون", desc: "طعم الليمون الحامض والمنعش." },
      strawberry: { name: "الفراولة", desc: "طعم الفراولة الحلو والطبيعي." }
    },
    tools: {
      hardCup500cc: { name: "كوب 500 مل", desc: "كوب 500 مل عالي الجودة لتقديم البابل تي." },
      hardCup700cc: { name: "كوب 700 مل", desc: "كوب 700 مل عالي الجودة لتقديم البابل تي." },
      lidPanda: { name: "غطاء", desc: "غطاء بتصميم باندا لطيف لإغلاق الأكواب بإحكام." },
      straw230: { name: "قش باللون", desc: "قش عريض مثالي لشفط كرات التابيوكا والمكونات." },
      powderScoop: { name: "ملعقة مسحوق", desc: "ملعقة قياس دقيقة لتحديد كمية النكهة المناسبة." },
      longBarSpoon: { name: "ملعقة طويلة", desc: "ملعقة طويلة احترافية لخلط المشروبات." },
      bobaSpoon: { name: "ملعقة بوبا", desc: "ملعقة مخصصة لتقديم كرات البوبا بسهولة." },
      jigger: { name: "الوالج", desc: "أداة قياس ضرورية لتحديد نسب الشراب والسوائل بدقة." },
      pitcher: { name: "إبريق صغير", desc: "إبريق متين لخلط وسكب قواعد الشاي." },
      shaker500cc: { name: "مل 500 شاكر", desc: "شاكر احترافي سعة 500 مل لخلط المشروبات الباردة." },
      shaker700cc: { name: "مل 700 شاكر", desc: "شاكر احترافي سعة 700 مل لخلط المشروبات الباردة." },
      plasticBag: { name: "كيس بلاستيكي", desc: "متوفر بنوعين: مفرد (لكوب واحد) ومزدوج (لكوبين معاً) لسهولة الحمل." },
      shakerMachine: { name: "آلة شاكر", desc: "آلة خلط أوتوماتيكية لتوفير الوقت وضمان الجودة." },
      thermos: { name: "الترماس", desc: "ترماس عالي الجودة للحفاظ على حرارة أو برودة الشاي لفترة طويلة." }
    },
    powders: {
      tea: { name: "اسام شاي اسود", desc: "طعم الشاي الأسود القوي والغني." },
      nonDairyCream: { name: "مبيضة غير ألبان", desc: "قوام كريمي غني وناعم." },
      chocolate: { name: "شوكولاتة", desc: "نكهة الكاكاو الغنية والفاخرة." },
      coffee: { name: "قهوة", desc: "طعم القهوة المحمصة القوي." },
      matcha: { name: "ماتشا", desc: "نكهة الشاي الأخضر الياباني الطبيعية." },
      banana: { name: "موز", desc: "طعم الموز الحلو والكريمي." },
      watermelon: { name: "رقي", desc: "نكهة الرقي الحلوة والمنعشة." },
      avocado: { name: "الأفوكادو", desc: "طعم الأفوكادو الغني والكريمي." },
      coconut: { name: "جوز الهند", desc: "نكهة جوز الهند الاستوائية الحلوة." },
      greenApple: { name: "التفاح الأخضر", desc: "طعم التفاح الأخضر المقرمش والحامض." },
      mango: { name: "مانجو", desc: "نكهة المانجو الاستوائية الحلوة." },
      strawberry: { name: "الفراولة", desc: "طعم الفراولة الحلو والكريمي." },
      blueberry: { name: "توت أزرق", desc: "نكهة توت أزرق مع لمسة حموضة." },
      taro: { name: "القلقاس", desc: "نكهة حلوة ومميزة تشبه الفانيليا." },
      peach: { name: "خوخ", desc: "طعم الخوخ الناعم والعطري." },
      pineapple: { name: "الأناناس", desc: "نكهة الأناناس الاستوائية المنعشة." },
      vanilla: { name: "فانيلا", desc: "طعم الفانيليا الكلاسيكي والناعم." },
      oreo: { name: "أوريو", desc: "نكهة بسكويت الشوكولاتة والكريم." },
      cherry: { name: "الكرز", desc: "طعم الكرز الفاكهي والحلو." },
      passionFruit: { name: "باشن فروت", desc: "نكهة باشن فروت الحامضة والمنعشة." },
      tiramisu: { name: "تيراميسو", desc: "نكهة كعكة التيراميسو مع القهوة والكاكاو." },
      redVelvet: { name: "ريد فيلفيت", desc: "نكهة كعكة الريد فيلفيت الحلوة." },
      sweetPotato: { name: "البطاطا الحلوة", desc: "طعم البطاطا الحلوة الغني والمميز." },
      yellowHamiMelon: { name: "البطيخ الأصفر", desc: "نكهة البطيخ الأصفر الحلوة والعطرية." }
    },
    bobas: {
      tapioca: { name: "تابوكا", desc: "كرات التابيوكا اللذيذة والمثالية للإضافة." },
      blueberryBoba: { name: "توت أزرق", desc: "كرات توت أزرق اللذيذة والمثالية للإضافة." },
      strawberryBoba: { name: "الفراولة", desc: "كرات الفراولة اللذيذة والمثالية للإضافة." },
      lycheeBoba: { name: "ليتشي", desc: "كرات الليتشي اللذيذة والمثالية للإضافة." },
      honeyDewBoba: { name: "البطيخ", desc: "كرات البطيخ اللذيذة والمثالية للإضافة." },
      mangoBoba: { name: "مانجو", desc: "كرات المانجو اللذيذة والمثالية للإضافة." },
      passionFruitBoba: { name: "باشن فروت", desc: "كرات باشن فروت اللذيذة والمثالية للإضافة." },
      kiwiBoba: { name: "كيوي", desc: "كرات الكيوي اللذيذة والمثالية للإضافة." },
      coffeeBoba: { name: "قهوة", desc: "كرات القهوة اللذيذة والمثالية للإضافة." },
      tropicalBoba: { name: "فواكه مشكلة", desc: "كرات الفواكه المشكلة اللذيذة والمثالية للإضافة." },
      pomegranateBoba: { name: "الرمان", desc: "كرات الرمان اللذيذة والمثالية للإضافة." },
      cherryBoba: { name: "الكرز", desc: "كرات الكرز اللذيذة والمثالية للإضافة." },
      chocolateBoba: { name: "شوكولاتة", desc: "كرات الشوكولاتة اللذيذة والمثالية للإضافة." },
      caramelBoba: { name: "كراميل", desc: "كرات الكراميل اللذيذة والمثالية للإضافة." },
      greenAppleBoba: { name: "تفاحة خضراء", desc: "كرات التفاحة الخضراء اللذيذة والمثالية للإضافة." },
      peachBoba: { name: "خَوْخ", desc: "كرات الخوخ اللذيذة والمثالية للإضافة." },
      raspberryBoba: { name: "توت العليق", desc: "كرات توت العليق اللذيذة والمثالية للإضافة." },
      coffeeJelly: { name: "قهوة", desc: "كرات القهوة اللذيذة والمثالية للإضافة." },
      rainbowJelly: { name: "مزيج الفاكهة", desc: "كرات مزيج الفاكهة اللذيذة والمثالية للإضافة." },
      nataDeCoco: { name: "جوزة الهند", desc: "كرات جوزة الهند اللذيذة والمثالية للإضافة." },
      nataDeCocoStrawberry: { name: "جوز الهند بالفراولة", desc: "كرات جوز الهند بالفراولة اللذيذة والمثالية للإضافة." },
      nataDeCocoMango: { name: "جوز الهند والمانجو", desc: "كرات جوز الهند والمانجو اللذيذة والمثالية للإضافة." }
    },
    footer: {
      about: "معلومات عنا",
      aboutDesc: "شريكك لنجاح أعمال بابل تي المهنية.",
      quickLinks: "روابط سريعة",
      contact: "اتصل بنا",
      rights: "جميع الحقوق محفوظة.",
      ctaTitle: "نتطلع إلى رسالتك!"
    }
  }
};


